import { FC, useEffect, MouseEvent, useState, useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import { MoreHoriz } from "@mui/icons-material";
import { useAppSelector } from "@/hooks/useAppSelector";
import PreviewLongPostFooter from "./PreviewLongPostFooter";
import LogoFull from "@/assets/svg/Logo/LogoFull";
import Article from "@/assets/svg/Article";
import { IPost } from "@/store/api/postApiData";
import { formatTimeAgo } from "@/helpers/getHoursDifference";
import { useBuyPostMutation } from "@/store/api/walletApi";
import CustomLoader from "../../CustomLoader/CustomLoader";
import EditPostSvg from "@/assets/svg/EditPostSvg";
import CreateLongPostModal from "./CreateLongPostModal";
import ReportPostSvg from "@/assets/svg/ReportPostSvg";
import { RequireAuth } from "@/components/RequireAuth";
import BasketSvg from "@/assets/svg/BasketSvg";
import { styles } from "./stylesPreviewLongPost";
import ReportModal from "../ReportModal/ReportModal";
import DeleteModal from "../DeleteModal/DeleteModal";
import { useDeletePostMutation } from "@/store/api/postApi";
import { safeParseHtml } from "@/helpers/safeParseHtml";
import ArbitraryBuyModal from "./ArbitraryBuyModal";

interface IPreviewLongPostProps {
  open: boolean;
  handleClose: () => void;
  imagePreview: string | null;
  title: string;
  content: string;
  chips: string[];
  isPaidContent: boolean;
  slider: number;
  editPost: IPost;
  price: string;
  summaryHtml: string;
  bodyHtml: string;
  setToast: (toast: {
    message: string;
    success: boolean;
    link?: { text: string; href: string };
  }) => void;
  isPreview: boolean;
  freeCharCount: number;
  imageBlurPercent?: number;
  allowAnyAmount?: boolean;
}

const truncateHtmlByChars = (html: string, maxChars: number, imageClipPercent = 0): string => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  let charCount = 0;

  const truncateNode = (node: Node): boolean => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      if (!text.trim()) return false;
      if (charCount + text.length <= maxChars) {
        charCount += text.length;
        return false;
      } else {
        const remainingChars = maxChars - charCount;
        node.textContent = text.slice(0, remainingChars);
        charCount = maxChars;
        return true;
      }
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.tagName === "IMG") {
        if (charCount + 1 <= maxChars) {
          charCount += 1;
          if (charCount === maxChars && imageClipPercent > 0 && imageClipPercent < 1) {
            const blurHeight = Math.round((1 - imageClipPercent) * 100);
            const wrapper = document.createElement("div");
            wrapper.style.position = "relative";
            wrapper.style.width = "100%";
            wrapper.style.marginTop = "50px";
            wrapper.style.marginBottom = "50px";
            wrapper.style.borderRadius = "16px";
            wrapper.style.overflow = "hidden";
            wrapper.style.height = "430px";
            el.style.margin = "0";
            el.style.borderRadius = "0";
            el.style.height = "100%";
            el.style.width = "100%";
            el.style.objectFit = "cover";
            el.style.display = "block";
            const overlay = document.createElement("div");
            overlay.style.position = "absolute";
            overlay.style.bottom = "0";
            overlay.style.left = "0";
            overlay.style.right = "0";
            overlay.style.height = `${blurHeight}%`;
            overlay.style.backdropFilter = "blur(8px)";
            overlay.style.setProperty("-webkit-backdrop-filter", "blur(8px)");
            el.parentNode?.insertBefore(wrapper, el);
            wrapper.appendChild(el);
            wrapper.appendChild(overlay);
          }
          return false;
        } else {
          el.remove();
          return true;
        }
      }
      const children = Array.from(node.childNodes);
      for (let i = 0; i < children.length; i++) {
        if (truncateNode(children[i])) {
          while (node.childNodes.length > i + 1) {
            node.removeChild(node.lastChild!);
          }
          return true;
        }
      }
    }
    return false;
  };

  truncateNode(tempDiv);
  return tempDiv.innerHTML;
};

const getHtmlFromCharOffset = (html: string, offset: number): string => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  let charCount = 0;

  const skipChars = (node: Node): boolean => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      if (!text.trim()) {
        node.textContent = "";
        return false;
      }
      if (charCount + text.length <= offset) {
        charCount += text.length;
        node.textContent = "";
        return false;
      } else {
        const charsToSkip = offset - charCount;
        node.textContent = text.slice(charsToSkip);
        charCount = offset;
        return true;
      }
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.tagName === "IMG") {
        if (charCount + 1 <= offset) {
          charCount += 1;
          el.remove();
          return false;
        }
        return true;
      }
      for (const child of Array.from(node.childNodes)) {
        if (skipChars(child)) return true;
      }
    }
    return false;
  };

  skipChars(tempDiv);

  // Only remove leading empty top-level nodes (content before the offset
  // that was emptied by skipChars). Preserve empty paragraphs within the
  // paid content that represent intentional spacing (double-enter).
  while (tempDiv.firstChild) {
    const child = tempDiv.firstChild;
    const isImg = child.nodeType === Node.ELEMENT_NODE && (child as HTMLElement).tagName === "IMG";
    const hasImg =
      child.nodeType === Node.ELEMENT_NODE && (child as HTMLElement).querySelector?.("img");
    if (!child.textContent?.trim() && !isImg && !hasImg) {
      tempDiv.removeChild(child);
    } else {
      break;
    }
  }

  return tempDiv.innerHTML;
};

const PreviewLongPost: FC<IPreviewLongPostProps> = ({
  open,
  imagePreview,
  handleClose,
  title,
  content,
  chips,
  isPaidContent,
  slider,
  editPost,
  price,
  freeCharCount,
  imageBlurPercent = 0,
  summaryHtml,
  bodyHtml,
  setToast,
  isPreview,
  allowAnyAmount,
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const lastThreeLike: string[] = editPost?.reactionsPreview ?? [];
  const MyPost = editPost && isPreview ? editPost.author.uid === user?.uid : false;
  const updatedPost = useAppSelector((state) =>
    editPost?.uid ? state.updatedPosts.posts[editPost.uid] : undefined
  );
  const isPurchased = (updatedPost ?? editPost)?.isPurchased ?? false;
  const extractBodyHtml = (rawContent: string): string => {
    const afterH2 = rawContent.replace(/<h2[^>]*>[\s\S]*?<\/h2>/i, "");
    return afterH2
      .replace(/<separator>[\s\S]*?<\/separator>/i, "")
      .replace(/^<br\s*\/?>/, "");
  };
  const avatarSrc = isPreview ? editPost?.author?.image?.url : (user?.image?.url ?? "");
  const username = editPost?.author?.username ?? user?.username ?? "";
  const toPublishTime = editPost?.createdAt ? formatTimeAgo(editPost.createdAt) : "2h";
  const [buyPost, { isLoading: isBuying, isSuccess }] = useBuyPostMutation();
  const [arbitraryBuyModalOpen, setArbitraryBuyModalOpen] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);

  const unlockedContent = updatedPost?.content;
  const isUnlocked = MyPost || isPurchased || (isSuccess && Boolean(unlockedContent));
  const effectiveBodyHtml =
    isUnlocked && unlockedContent ? extractBodyHtml(unlockedContent) : bodyHtml;

  const isProcessing = isBuying || isFinalizing;
  useEffect(() => {
    if (isFinalizing && isUnlocked) {
      setIsFinalizing(false);
      setArbitraryBuyModalOpen(false);
    }
  }, [isFinalizing, isUnlocked]);

  const [editLongPostModal, setEditLongPostModal] = useState(false);
  //@ts-ignore
  const [editLongPost, setEditLongPost] = useState<Record<string, any>>({});
  const [deletePost] = useDeletePostMutation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const [openReportModal, setOpenReportModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [reportPostUid, setReportPostUid] = useState("");

  const bottomBuyButtonRef = useRef<HTMLButtonElement | null>(null);
  const [showTopButton, setShowTopButton] = useState(false);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  const handleReport = (uid: string) => {
    setReportPostUid(uid);
    setOpenReportModal(true);
    handleCloseMenu();
  };

  const handleDelete = () => {
    setOpenDeleteModal(true);
    handleCloseMenu();
  };
  const handleBuyClick = async () => {
    if (!editPost?.uid) return;

    if (editPost?.isArbitraryPrice) {
      setArbitraryBuyModalOpen(true);
      return;
    }

    try {
      setIsFinalizing(true);
      const response = await buyPost({ postId: editPost.uid, amountSats: Number(price) }).unwrap();
      setToast({
        message: response.message ?? "Purchase completed successfully",
        success: response.success,
      });
    } catch (err: any) {
      setIsFinalizing(false);
      const message = err?.data?.message || "Something went wrong";
      setToast({
        message,
        success: false,
      });
    }
  };

  const handleArbitraryPriceBuy = async (amount: number) => {
    if (!editPost?.uid) return;

    try {
      setIsFinalizing(true);
      const response = await buyPost({ postId: editPost.uid, amountSats: amount }).unwrap();
      setToast({
        message: response.message ?? "Purchase completed successfully",
        success: response.success,
      });
    } catch (err: any) {
      setIsFinalizing(false);
      setArbitraryBuyModalOpen(false);
      const message = err?.data?.message || "Something went wrong";
      setToast({
        message,
        success: false,
      });
    }
  };

  useEffect(() => {
    if (isPaidContent) {
      const timer = setTimeout(() => {}, 100);

      return () => clearTimeout(timer);
    }
  }, [open, isPaidContent, content]);
  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    handleClose();
  };
  const handleCloseReportModal = () => setOpenReportModal(false);
  const handleConfirmDelete = async () => {
    // if (onDelete) {
    //   onDelete(post.uid);
    // }

    try {
      await deletePost(editPost.uid).unwrap();
    } catch (error) {
      console.error("Delete failed", error);
    } finally {
      setOpenDeleteModal(false);
    }
  };

  useEffect(() => {
    if (!isPaidContent || isUnlocked) {
      setShowTopButton(false);
      return;
    }

    const el = bottomBuyButtonRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowTopButton(!entry.isIntersecting);
      },
      { threshold: 0.01 }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [isPaidContent, effectiveBodyHtml, open, isUnlocked]);

  const splitBodyContent = (): { visibleBody: string; blurredBody: string } => {
    if (isUnlocked) {
      return { visibleBody: effectiveBodyHtml, blurredBody: "" };
    }

    if (!isPaidContent) {
      return { visibleBody: effectiveBodyHtml, blurredBody: "" };
    }

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = effectiveBodyHtml;
    let totalChars = 0;
    const countChars = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || "";
        if (text.trim()) totalChars += text.length;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        if (el.tagName === "IMG") {
          totalChars += 1;
        } else {
          for (const child of Array.from(node.childNodes)) countChars(child);
        }
      }
    };
    countChars(tempDiv);

    if (freeCharCount <= 0) {
      return { visibleBody: "", blurredBody: effectiveBodyHtml };
    }

    if (freeCharCount >= totalChars) {
      return { visibleBody: effectiveBodyHtml, blurredBody: "" };
    }

    return {
      visibleBody: truncateHtmlByChars(effectiveBodyHtml, freeCharCount, imageBlurPercent),
      blurredBody: getHtmlFromCharOffset(effectiveBodyHtml, freeCharCount),
    };
  };

  const { visibleBody, blurredBody } = splitBodyContent();

  const hasBlurredContent = blurredBody.length > 0 && isPaidContent;

  // const formattedPrice = new Intl.NumberFormat("de-DE").format(Number(price));
  const parsedTitle = safeParseHtml(title);

  return (
    <Box
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        ...styles.postWrapper,
        ...(!isPreview && { pointerEvents: "none" }),
      }}
    >
      <Modal
        open={arbitraryBuyModalOpen}
        onClose={() => setArbitraryBuyModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "#0000001A" },
          },
        }}
      >
        <ArbitraryBuyModal
          price={price}
          onSubmit={handleArbitraryPriceBuy}
          onHeaderClick={() => setArbitraryBuyModalOpen(false)}
          isLoading={isProcessing}
        />
      </Modal>
      <Box sx={styles.postContainer}>
        <ReportModal
          handleClose={handleCloseReportModal}
          open={openReportModal}
          postUid={reportPostUid}
          variant="post"
        />

        <DeleteModal
          open={openDeleteModal}
          handleClose={handleCloseDeleteModal}
          onDelete={handleConfirmDelete}
          variant="post"
          title="Are you sure you want to delete the post?"
          description="You won't be able to recover it"
        />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={styles.headerStack}
        >
          <Stack direction="row" sx={styles.userInfoStack}>
            <Avatar src={avatarSrc} alt="Profile" sx={{ width: 48, height: 48 }} />
            <Typography sx={{ fontWeight: "600", fontSize: "1.125rem" }}>{user?.name}</Typography>
            <Typography sx={styles.usernameText}>{username}</Typography>
            <Typography sx={styles.userTimeText}>• {toPublishTime}.</Typography>
          </Stack>

          <IconButton
            size="small"
            aria-label="more options"
            aria-controls={open ? "post-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
          >
            <MoreHoriz />
          </IconButton>

          <Menu
            id="post-menu"
            anchorEl={anchorEl}
            open={isOpen}
            onClose={handleCloseMenu}
            MenuListProps={{
              "aria-labelledby": "post-menu-button",
              sx: {
                paddingTop: 0,
                paddingBottom: 0,
              },
            }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            PaperProps={styles.postSelectDropdownMenu}
          >
            {MyPost
              ? [
                  <MenuItem
                    key="edit"
                    onClick={() => {
                      setEditLongPost({ ...editPost, open: true });
                      setEditLongPostModal(true);
                      handleCloseMenu();
                    }}
                    sx={styles.menuItemEdit}
                  >
                    <ListItemIcon sx={{ minWidth: "0px !important" }}>
                      <EditPostSvg height={16} width={16} />
                    </ListItemIcon>
                    <ListItemText>
                      <Typography sx={styles.menuOptionText}>Edit Article</Typography>
                    </ListItemText>
                  </MenuItem>,
                  <MenuItem
                    key="delete"
                    onClick={() => {
                      handleDelete();
                      handleCloseMenu();
                    }}
                    sx={styles.menuItemDelete}
                  >
                    <ListItemIcon sx={{ minWidth: "0px !important" }}>
                      <BasketSvg style={{ width: "1rem", height: "1rem" }} />
                    </ListItemIcon>
                    <ListItemText>
                      <Typography color="error" sx={styles.menuOptionText}>
                        Delete Article
                      </Typography>
                    </ListItemText>
                  </MenuItem>,
                ]
              : [
                  <RequireAuth key="report">
                    <MenuItem
                      onClick={() => {
                        handleReport(editPost.uid);
                        handleCloseMenu();
                      }}
                      sx={{ gap: "0.25rem", padding: "1rem 1.3314rem" }}
                    >
                      <ListItemIcon sx={{ minWidth: "0px !important" }}>
                        <ReportPostSvg />
                      </ListItemIcon>
                      <ListItemText>
                        <Typography sx={{ ...styles.menuOptionText, color: "#CE212B" }}>
                          Report a post
                        </Typography>
                      </ListItemText>
                    </MenuItem>
                  </RequireAuth>,
                ]}
          </Menu>
        </Stack>

        {imagePreview && (
          <Box sx={styles.imageWrapper}>
            <img src={imagePreview} alt="post_image" style={styles.imageStyle} />
            {/* watermark */}
            <Box sx={styles.watermark}>
              <Box sx={styles.logoBox}>
                <LogoFull style={{ width: "100%", height: "100%" }} />
              </Box>
              {isPaidContent && (
                <Box sx={styles.paidBox}>
                  <Article />
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            width: "100%",
            "& .preview-content": {
              fontSize: "15px",
            },
            "& .preview-content > br": {
              display: "block",
              height: "22.5px",
            },
            "& .preview-content img": {
              width: "100%",
              height: "430px",
              objectFit: "cover",
              borderRadius: "16px",
              display: "block",
              marginTop: "50px",
              marginBottom: "50px",
            },
          }}
        >
          <Typography
            // variant="h2"
            component={"p"}
            sx={{
              textAlign: "left",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              fontWeight: 600,
              margin: "0 0 16px 0",
              fontSize: "26px",
              marginBlockStart: "0",
              marginInlineEnd: "0",
              "& p": {
                margin: 0,
              },
            }}
          >
            {parsedTitle}
          </Typography>

          {summaryHtml && <div className="preview-content">{safeParseHtml(summaryHtml)}</div>}

          {summaryHtml && bodyHtml && <br />}

          <Box sx={styles.bodyWrapper}>
            {visibleBody && <div className="preview-content">{safeParseHtml(visibleBody)}</div>}

            {hasBlurredContent && !isUnlocked && !isProcessing && (
              <>
                {showTopButton && (
                  <Button onClick={handleBuyClick} disabled={isBuying} sx={{ ...styles.buyButton }}>
                    <Typography sx={styles.buyButtonText}>
                      {!(editPost?.isArbitraryPrice || allowAnyAmount)
                        ? `Get full for ${Number(price)} USDC`
                        : "Unlock full article"}
                    </Typography>
                  </Button>
                )}
                <Box
                  sx={{
                    filter: "blur(8px)",
                    userSelect: "none",
                    pointerEvents: "none",
                  }}
                  className="preview-content"
                >
                  {safeParseHtml(blurredBody)}
                </Box>
              </>
            )}

            {hasBlurredContent && !isUnlocked && isProcessing && (
              <Box sx={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                <CustomLoader loading size={40} isModal={false} color="#4285f4" />
              </Box>
            )}
          </Box>
        </Box>
        {isPaidContent && !isUnlocked && !isProcessing && (
          <Box sx={styles.btnWrapper}>
            <Button
              ref={bottomBuyButtonRef}
              onClick={handleBuyClick}
              disabled={isBuying}
              sx={styles.buyButton}
            >
              <Typography sx={styles.buyButtonText}>
                {!(editPost?.isArbitraryPrice || allowAnyAmount)
                  ? `Get full for ${Number(price)} USDC `
                  : "Unlock full article"}
              </Typography>
            </Button>
          </Box>
        )}
        <PreviewLongPostFooter
          post={editPost ?? undefined}
          lastThreeLike={lastThreeLike}
          isPaidContent={isPaidContent}
          chips={chips}
          slider={slider}
          price={price}
          totalLikesAmount={editPost?.totalLikesAmount ?? 0}
        />
      </Box>

      {editLongPostModal && (
        <CreateLongPostModal
          open={editLongPostModal}
          handleClose={() => {
            setEditLongPostModal(false);
            setEditLongPost({ ...editPost, open: false });
          }}
          editPost={editPost}
          setToast={setToast}
          setIsLongPostModalOpen={setEditLongPostModal}
        />
      )}
    </Box>
  );
};

export default PreviewLongPost;
